import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User, LoginResult } from '@/types'
import { authApi } from '@/services/api'

interface AuthStore {
  user: User | null
  token: string | null
  refreshToken: string | null
  isLoading: boolean
  isAuthenticated: boolean

  login: (email: string, password: string, mfaCode?: string) => Promise<LoginResult>
  logout: () => void
  refreshAuth: () => Promise<void>
  setUser: (user: User) => void
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      refreshToken: null,
      isLoading: false,
      isAuthenticated: false,

      login: async (email: string, password: string, mfaCode?: string) => {
        set({ isLoading: true })
        try {
          const response = await authApi.login(email, password, mfaCode)
          const result = response.data.data!

          if (result.mfaRequired) {
            set({ isLoading: false })
            return result
          }

          set({
            user: result.user,
            token: result.token,
            refreshToken: result.refreshToken,
            isAuthenticated: true,
            isLoading: false,
          })

          localStorage.setItem('agrocredit:token', result.token)
          localStorage.setItem('agrocredit:refreshToken', result.refreshToken)

          return result
        } catch (error) {
          set({ isLoading: false })
          throw error
        }
      },

      logout: () => {
        authApi.logout().catch(() => {})
        localStorage.removeItem('agrocredit:token')
        localStorage.removeItem('agrocredit:refreshToken')
        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
        })
      },

      refreshAuth: async () => {
        const currentRefreshToken = get().refreshToken
        if (!currentRefreshToken) throw new Error('No refresh token')

        try {
          const response = await authApi.refreshToken(currentRefreshToken)
          const result = response.data.data!

          set({
            token: result.token,
            refreshToken: result.refreshToken,
            user: result.user,
            isAuthenticated: true,
          })

          localStorage.setItem('agrocredit:token', result.token)
          localStorage.setItem('agrocredit:refreshToken', result.refreshToken)
        } catch {
          get().logout()
        }
      },

      setUser: (user: User) => set({ user }),
    }),
    {
      name: 'agrocredit:auth',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
)
